import cx from 'classnames/bind';
import React from 'react';
import styles from './ErrorScreen.scss';
import { CommandBarButton } from 'mini-command-bar-button';
import type { ErrorScreenProps, ErrorScreenText } from '../schema/ErrorScreen.types';
import { ErrorType } from '../schema/ErrorScreen.types';
import { getOwaMiniCommonStrings } from 'owa-strings/owa-mini-common-strings';
import { hardRefresh } from '../utils/hardRefresh';

const classNames = cx.bind(styles);

interface BaseErrorScreenProps extends ErrorScreenProps {
    errorType?: ErrorType;
}

/**
 * Functional Component to display the base error screen
 * @param props Error Screen Props
 */
const BaseErrorScreen = (props: BaseErrorScreenProps): JSX.Element => {
    const strings = getOwaMiniCommonStrings();
    // Destructuring props
    const {
        image,
        errorType = ErrorType.Generic,
        actionButton = {},
        showDefaultActionButton = true,
    } = props;
    const {
        title = getDefaultPrimaryTitle(errorType),
        subtitle = getDefaultSecondaryTitle(errorType),
    } = props;

    // action button
    const {
        label,
        icon,
        onClick: onClickProp,
        ariaLabel: actionButtonAriaLabel,
        className: actionButtonStyles,
        ariaHasPopup = false,
    } = actionButton;

    const showActionButton = !!(onClickProp && label) || showDefaultActionButton;
    const onClick = () => {
        (onClickProp || hardRefresh)();
    };
    const actionLabel = label ?? strings.retryButton;

    return (
        <>
            {image}
            <Title {...title} className={classNames(styles.title, title.className)} />
            <Title {...subtitle} className={classNames(styles.subtitle, subtitle.className)} />
            {showActionButton && (
                <CommandBarButton
                    ariaHasPopup={ariaHasPopup}
                    icon={icon}
                    label={actionLabel}
                    onClick={onClick}
                    className={classNames(styles.button, actionButtonStyles)}
                    ariaLabel={actionButtonAriaLabel}
                />
            )}
        </>
    );
};

function Title(props: ErrorScreenText) {
    const { text: title, ref: titleRef, className: titleStyles } = props;

    return (
        <div ref={titleRef} className={titleStyles}>
            {title}
        </div>
    );
}
function getDefaultPrimaryTitle(errorType: ErrorType): ErrorScreenText {
    const strings = getOwaMiniCommonStrings();

    if (errorType === ErrorType.Network) {
        return { text: strings.errorBoundaryNetworkErrorPrimaryHeader };
    }

    return { text: strings.errorBoundaryHeader };
}

function getDefaultSecondaryTitle(errorType: ErrorType): ErrorScreenText {
    const strings = getOwaMiniCommonStrings();

    if (errorType === ErrorType.Network) {
        return { text: strings.errorBoundaryNetworkErrorSecondaryHeader };
    }

    return { text: strings.genericErrorSecondaryHeader };
}

export { BaseErrorScreen };
